import type {
  DatabaseMetaInfo,
  EnumColumnDeclaration,
  EnumGlyphDismissal,
  EnumLookupFetchTarget,
  EnumValueCache,
  EnumValueSource,
  JoinTableHintRecord,
  JsonColumnDeclaration,
  JsonStructureCache,
  LooseRelationship,
  MetaInfoDelta,
  ReferenceTableDeclaration,
  SpatialColumnDeclaration,
  TableDisplayColumn,
} from '@/types/electron';

const api = () => {
  const ea = window.electronAPI;
  if (!ea?.metainfo) {
    throw new Error('metainfo IPC unavailable (web mode)');
  }
  return ea.metainfo;
};

export const fetchMetaInfo = (connectionId: string, database: string) =>
  api().get(connectionId, database);

export const fetchAllMetaInfo = () => api().listAll();

export const addLooseRelationship = (
  connectionId: string,
  database: string,
  rel: LooseRelationship,
) => api().addLooseRelationship(connectionId, database, rel);

export const updateLooseRelationship = (
  connectionId: string,
  database: string,
  id: string,
  patch: Partial<LooseRelationship>,
) => api().updateLooseRelationship(connectionId, database, id, patch);

export const removeLooseRelationship = (
  connectionId: string,
  database: string,
  id: string,
) => api().removeLooseRelationship(connectionId, database, id);

export const addJsonColumn = (
  connectionId: string,
  database: string,
  decl: JsonColumnDeclaration,
) => api().addJsonColumn(connectionId, database, decl);

export const removeJsonColumn = (
  connectionId: string,
  database: string,
  id: string,
) => api().removeJsonColumn(connectionId, database, id);

export const upsertJsonStructureCache = (
  connectionId: string,
  database: string,
  entry: JsonStructureCache,
) => api().upsertJsonStructureCache(connectionId, database, entry);

export const addEnumColumn = (
  connectionId: string,
  database: string,
  decl: EnumColumnDeclaration,
) => api().addEnumColumn(connectionId, database, decl);

export const removeEnumColumn = (
  connectionId: string,
  database: string,
  id: string,
) => api().removeEnumColumn(connectionId, database, id);

export const addSpatialColumn = (
  connectionId: string,
  database: string,
  decl: SpatialColumnDeclaration,
) => api().addSpatialColumn(connectionId, database, decl);

export const removeSpatialColumn = (
  connectionId: string,
  database: string,
  id: string,
) => api().removeSpatialColumn(connectionId, database, id);

export const addEnumGlyphDismissal = (
  connectionId: string,
  database: string,
  key: Pick<EnumGlyphDismissal, 'schema' | 'table' | 'column'>,
) => api().addEnumGlyphDismissal(connectionId, database, key);

export const removeEnumGlyphDismissal = (
  connectionId: string,
  database: string,
  key: Pick<EnumGlyphDismissal, 'schema' | 'table' | 'column'>,
) => api().removeEnumGlyphDismissal(connectionId, database, key);

export const updateEnumColumn = (
  connectionId: string,
  database: string,
  id: string,
  patch: Partial<EnumColumnDeclaration>,
) => api().updateEnumColumn(connectionId, database, id, patch);

export const upsertEnumValueCache = (
  connectionId: string,
  database: string,
  entry: EnumValueCache,
) => api().upsertEnumValueCache(connectionId, database, entry);

export const removeEnumValueCache = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
  path?: string,
) => api().removeEnumValueCache(connectionId, database, schema, table, column, path);

export const fetchEnumValues = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
  engine: string,
  source: EnumValueSource | 'auto',
  path?: string,
  pathKind?: 'scalar' | 'array',
  /** Required when `source === 'lookup'` — the FK target the key/label pairs come from. */
  lookup?: EnumLookupFetchTarget,
) => api().fetchEnumValues(connectionId, database, schema, table, column, engine, source, path, pathKind, lookup);

export const upsertTableDisplayColumn = (
  connectionId: string,
  database: string,
  entry: TableDisplayColumn,
) => api().upsertTableDisplayColumn(connectionId, database, entry);

export const removeTableDisplayColumn = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
) => api().removeTableDisplayColumn(connectionId, database, schema, table);

export const addReferenceTable = (
  connectionId: string,
  database: string,
  decl: ReferenceTableDeclaration,
) => api().addReferenceTable(connectionId, database, decl);

export const removeReferenceTable = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
) => api().removeReferenceTable(connectionId, database, schema, table);

export const upsertJoinTableHint = (
  connectionId: string,
  database: string,
  entry: JoinTableHintRecord,
) => api().upsertJoinTableHint(connectionId, database, entry);

export const removeJoinTableHint = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
) => api().removeJoinTableHint(connectionId, database, schema, table);

export const exportMetaInfo = (connectionId: string, database: string) =>
  api().export(connectionId, database);

export const importMetaInfo = (
  connectionId: string,
  database: string,
  json: string,
  mode: 'merge' | 'replace',
): Promise<{ metaInfo: DatabaseMetaInfo; warnings: Array<{ relationshipId: string; side: 'source' | 'target'; missingColumn: string }> }> =>
  // The dynamic preload wrapper makes satisfying the IPC barrel type difficult;
  // `as never` is an intentional renderer-only narrowing.
  api().import(connectionId, database, json, mode) as never;

/**
 * S8b.T2 — merges a `MetaInfoDelta` (enum/JSON/loose-relationship additions + their caches) into
 * the current record in ONE atomic write. Used by `MetaInfoSuggestionsCard` to save the
 * user-accepted subset of a just-created new-table import's post-success suggestions.
 */
export const applyMetaInfoDelta = (connectionId: string, database: string, delta: MetaInfoDelta) =>
  api().applyMetaInfoDelta(connectionId, database, delta);

/**
 * Call `onWritten` after every committed DECLARATION write to one
 * `(connectionId, database)` MetaInfo record — from any surface, MCP and
 * imports included (the main process broadcasts `metainfo:written` at the
 * store's commit). Derived-cache writes (`kind: 'cache'`) are skipped: the
 * grids' JSON-structure aggregator commits one every few seconds, and a
 * subscriber that refetches on those feeds a write → refetch → re-sample loop
 * (see `useMetaInfoDeclareApplied`). Returns the unsubscribe; a no-op in web
 * mode, which has no MetaInfo IPC.
 */
export function onMetaInfoDeclarationWritten(
  connectionId: string,
  database: string,
  onWritten: () => void,
): () => void {
  const subscribe = typeof window === 'undefined' ? undefined : window.electronAPI?.metainfo?.onWritten;
  if (!subscribe) return () => {};
  return subscribe((writtenConnectionId, writtenDatabase, kind) => {
    if (kind !== 'declaration') return;
    if (writtenConnectionId !== connectionId || writtenDatabase !== database) return;
    onWritten();
  });
}

export type {
  DatabaseMetaInfo,
  EnumColumnDeclaration,
  EnumValueCache,
  EnumValueSource,
  JoinTableHintRecord,
  JsonColumnDeclaration,
  JsonStructureCache,
  LooseRelationship,
  ReferenceTableDeclaration,
  SpatialColumnDeclaration,
  TableDisplayColumn,
};
